import React, { useState, useEffect, useRef } from 'react';

const TextColorDropdown = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const dropdownRef = useRef(null);

  // Predefined color palette
  const colorPalette = [
    { name: 'Black', value: '#000000' },
    { name: 'Dark Gray', value: '#374151' },
    { name: 'Medium Gray', value: '#6B7280' },
    { name: 'Light Gray', value: '#9CA3AF' },
    { name: 'Red', value: '#DC3545' },
    { name: 'Tuscan Orange', value: '#CC7722' },
    { name: 'Sunglow', value: '#FFCC33' },
    { name: 'Pear', value: '#D1E231' },
    { name: 'Eucalyptus', value: '#44883E' },
    { name: 'Faded Jade', value: '#427D5B' },
    { name: 'Fan Blue', value: '#4F97A3' },
    { name: 'Tyrian Purple', value: '#66023C' }
  ];

  useEffect(() => {
    if (!editor) return;

    const updateCurrentColor = () => {
      const color = editor.getAttributes('textStyle').color;
      setCurrentColor(color || '#000000');
    };

    updateCurrentColor();
    editor.on('selectionUpdate', updateCurrentColor);
    editor.on('transaction', updateCurrentColor);

    return () => {
      editor.off('selectionUpdate', updateCurrentColor);
      editor.off('transaction', updateCurrentColor);
    };
  }, [editor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!editor) return null;

  const applyColor = (color) => {
    if (color === '#000000') {
      // Remove color styling for black (default)
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(color).run();
    }
    setCurrentColor(color);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="dropdown position-relative" ref={dropdownRef}>
      {/* Color Button */}
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center justify-content-center border-0"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        title="Text Colour"
        style={{
          height: '32px',
          width: '32px',
          fontSize: '14px',
          fontWeight: 'bold',
          padding: '4px',
          backgroundColor: 'transparent',
          position: 'relative'
        }}>
        {/* A letter with color indicator */}
        <span style={{ color: currentColor, fontSize: '16px', fontWeight: 'bold' }}>A</span>
        {/* Color bar underneath */}
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '20px',
            height: '3px',
            backgroundColor: currentColor,
            borderRadius: '1px'
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            minWidth: '220px',
            padding: '8px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            marginTop: '2px',
            boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
            zIndex: 99999
          }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '4px',
              marginBottom: '8px'
            }}>
            {colorPalette.map((color, index) => (
              <button
                key={index}
                type="button"
                className="btn p-0"
                onClick={() => applyColor(color.value)}
                title={color.name}
                style={{
                  width: '28px',
                  height: '28px',
                  backgroundColor: color.value,
                  border: currentColor === color.value ? '2px solid #007bff' : '1px solid #dee2e6',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  boxShadow: currentColor === color.value ? '0 0 0 1px #007bff' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (currentColor !== color.value) {
                    e.target.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextColorDropdown;
